/* ═══════════════════════════════════════════════════════════════
   The Designer — Related sections (the one smart block that returned,
   Josh 2026-10-02; docs/MODULES_VISION.md §5).

   In a project spec the block lists the project's OTHER sections, live.
   Clicking it opens this picker: one row per section, a checkbox each,
   Include all / Exclude all, a search field once the list is long, and a
   "New" badge on sections added to the project since the writer last
   looked. Every tick is an ordinary editor transaction on the block's
   `smartParams` — it dirties the doc, autosaves, and Ctrl+Z undoes it.

   The selection rules and the default ("sections already in the project
   start ON, sections added later start OFF") are the pure functions in
   shared/related-sections.ts; this file is the wiring:
     · useRelatedSections — feeds the node views, inserts the block seeded
       with the project's sections (hook 3b), and seeds any unseeded block
       met in this editor (hook 3c, the fallback)
     · RelatedSectionsPicker — the popover (kit Popover, anchored to the
       block, portalled, viewport-clamped, Esc closes and hands focus back)
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Popover, PopoverAnchor, PopoverContent } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import {
  isRelatedSectionsBlock,
  isSeededFor,
  markRelatedSeen,
  readRelatedParams,
  relatedCandidates,
  seedRelatedParams,
  selectRelatedSections,
  setAllRelatedSections,
  toggleRelatedSection,
  type SectionRef,
} from '../../shared/related-sections';
import { insertRelatedSectionsBlock, setBlockSmartParams } from './commands';
import { createRelatedSectionsStore, type RelatedSectionsStore } from './relatedSectionsStore';

/** Above this many rows the picker offers a search field. */
const SEARCH_THRESHOLD = 8;

/** A Related sections node in the live document, by block id. */
function findRelatedNode(editor: Editor, blockId: string): { node: PMNode; pos: number } | null {
  let hit: { node: PMNode; pos: number } | null = null;
  editor.state.doc.forEach((node, pos) => {
    if (hit || node.type.name !== 'smartBlock' || node.attrs.blockId !== blockId) return;
    const meta = (node.attrs.meta as Record<string, unknown> | undefined) || {};
    if (
      isRelatedSectionsBlock({
        blockType: 'smart-token',
        smartBlockKind: meta.smartBlockKind as string,
      })
    )
      hit = { node, pos };
  });
  return hit;
}

function rawParams(node: PMNode): unknown {
  return ((node.attrs.meta as Record<string, unknown> | undefined) || {}).smartParams;
}

/**
 * Hook 3c — the fallback. Any Related sections block in this document that
 * is not seeded FOR THIS SPEC (never seeded, pasted in from a master, or
 * seeded in a different project spec entirely) is seeded now with this
 * spec's sections. It never runs on mere document open (fix-round, see
 * `useRelatedSections` below) — only riding a genuine edit the writer
 * already made, outside the undo history: it is bookkeeping, not an edit
 * the writer made.
 */
export function seedUnseededBlocks(editor: Editor, candidates: SectionRef[], key: string) {
  if (editor.isDestroyed || !editor.isEditable) return;
  const dispatch = editor.view.dispatch.bind(editor.view);
  const pending: string[] = [];
  editor.state.doc.forEach((node) => {
    if (node.type.name !== 'smartBlock') return;
    const meta = (node.attrs.meta as Record<string, unknown> | undefined) || {};
    if (
      isRelatedSectionsBlock({
        blockType: 'smart-token',
        smartBlockKind: meta.smartBlockKind as string,
      })
    )
      pending.push(node.attrs.blockId as string);
  });
  for (const blockId of pending) {
    const hit = findRelatedNode(editor, blockId);
    if (!hit) continue;
    const raw = rawParams(hit.node);
    const seeded = seedRelatedParams(raw, candidates, key);
    if (seeded === raw) continue;
    setBlockSmartParams(editor.state, dispatch, blockId, seeded, { history: false });
  }
}

export interface UseRelatedSections {
  store: RelatedSectionsStore;
  /** Insert a Related sections block after the current block (hook 3b). */
  insert: () => void;
}

export function useRelatedSections({
  editor,
  thisSpecId,
  projectSpecs,
  ready,
  canEdit,
}: {
  editor: Editor | null;
  thisSpecId: string;
  projectSpecs: SectionRef[];
  /** The project's spec list has been read at least once. */
  ready: boolean;
  canEdit: boolean;
}): UseRelatedSections {
  const storeRef = React.useRef<RelatedSectionsStore | null>(null);
  if (!storeRef.current) storeRef.current = createRelatedSectionsStore();
  const store = storeRef.current;

  const candidates = React.useMemo(
    () => relatedCandidates(thisSpecId, projectSpecs),
    [thisSpecId, projectSpecs]
  );

  // Every node view redraws from here — the live part of "live list".
  React.useEffect(() => {
    store.set({ status: ready ? 'ready' : 'loading', candidates, canEdit, key: thisSpecId });
  }, [store, ready, candidates, canEdit, thisSpecId]);

  // Switching sections is a new document: whatever picker was open belonged
  // to the old one.
  React.useEffect(() => store.close(), [store, thisSpecId]);

  // Hook 3c — the fallback, fix-round: merely opening this document dispatches
  // nothing and saves nothing. It only rides the writer's OWN next
  // doc-changing transaction (a keystroke, a restore, any real edit) — never
  // on mount, never on a remote/sync-driven refresh (those never reach the
  // editor as a transaction; the list is re-read via React state, not a doc
  // replace). Deferred a tick so it never dispatches inside a dispatch.
  React.useEffect(() => {
    if (!editor || !canEdit || !ready) return;
    let queued = false;
    const run = () => {
      queued = false;
      seedUnseededBlocks(editor, candidates, thisSpecId);
    };
    const onUpdate = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (!transaction.docChanged || queued) return;
      queued = true;
      queueMicrotask(run);
    };
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor, canEdit, ready, candidates, thisSpecId]);

  const insert = React.useCallback(() => {
    if (!editor) return;
    const snap = store.get();
    // Hook 3b: inserted inside a project spec → seeded with every section
    // currently in the project. Not loaded yet → unseeded; 3c seeds it on
    // the writer's next edit, once the list arrives.
    const params =
      snap.status === 'ready'
        ? seedRelatedParams({}, snap.candidates, thisSpecId)
        : unseededRelatedParams();
    insertRelatedSectionsBlock(editor.state, editor.view.dispatch.bind(editor.view), params);
    editor.commands.focus();
  }, [editor, store, thisSpecId]);

  return { store, insert };
}

/** Params for a block inserted where there is no project to seed from — a
 *  master or variant (Spec Studio), or a project list not loaded yet. The
 *  add-to-project write (hook 3a) or the fallback (3c) seeds it later. */
export function unseededRelatedParams(): Record<string, unknown> {
  return { seeded: false, includeIds: [], knownIds: [] };
}

/* ─── The picker ─── */

export function RelatedSectionsPicker({
  editor,
  store,
}: {
  editor: Editor | null;
  store: RelatedSectionsStore;
}) {
  const snap = React.useSyncExternalStore(store.subscribe, store.get);
  const open = React.useSyncExternalStore(store.subscribe, store.getOpen);
  // The block's params live in the document; follow every transaction.
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', tick);
    return () => {
      editor.off('transaction', tick);
    };
  }, [editor]);

  const [query, setQuery] = React.useState('');
  /** Sections that were new when the picker opened — they keep their badge
   *  for as long as it stays open, even though opening marks them seen. */
  const [freshAtOpen, setFreshAtOpen] = React.useState<Set<string>>(() => new Set());
  const contentRef = React.useRef<HTMLDivElement>(null);
  const closedByEscape = React.useRef(false);
  const titleId = React.useId();

  const hit = editor && open ? findRelatedNode(editor, open.blockId) : null;
  const raw = hit ? rawParams(hit.node) : undefined;
  const params = readRelatedParams(raw);
  const { candidates, canEdit, key: specKey } = snap;
  const ready = snap.status === 'ready';
  const writable = canEdit && ready && !!editor?.isEditable;

  const write = React.useCallback(
    (next: Record<string, unknown>, history: boolean) => {
      if (!editor || !open) return;
      setBlockSmartParams(
        editor.state,
        editor.view.dispatch.bind(editor.view),
        open.blockId,
        next,
        {
          history,
        }
      );
    },
    [editor, open]
  );

  // Opening = "seen": remember what was new (it keeps its badge while the
  // picker stays open), then record it on the block. Once per opened block —
  // a section that arrives while the picker is open stays badged, and the
  // close records it.
  const openKey = open ? open.blockId : '';
  React.useEffect(() => {
    if (!openKey || !editor) return;
    setQuery('');
    const found = findRelatedNode(editor, openKey);
    if (!found) return;
    const r = rawParams(found.node);
    const p = readRelatedParams(r);
    const known = new Set(p.knownIds || []);
    const seededHere = isSeededFor(p, specKey);
    setFreshAtOpen(
      new Set(seededHere ? candidates.filter((s) => !known.has(s.id)).map((s) => s.id) : [])
    );
    // Opening the picker with edit rights writes `knownIds` only — never the
    // seed itself (fix-round: a view-only open, or an open on a block that
    // isn't seeded for this spec, writes nothing at all).
    if (writable) {
      const next = markRelatedSeen(r, candidates, specKey);
      if (next !== r) write(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opened block
  }, [openKey]);

  // The block was deleted (or undone away) under an open picker.
  React.useEffect(() => {
    if (open && editor && !hit) store.close();
  });

  // Enter on a selected block asks for focus after the picker is already up.
  React.useEffect(() => {
    if (!open?.focus) return;
    const el = contentRef.current?.querySelector<HTMLElement>(
      'input, button[role="checkbox"]:not([disabled]), button:not([disabled])'
    );
    el?.focus();
  }, [open?.focus, open?.blockId]);

  const anchorRef = React.useMemo(() => ({ current: open?.anchor ?? null }), [open?.anchor]);

  if (!open || !hit) return null;

  const on = new Set(selectRelatedSections(params, candidates, specKey).map((s) => s.id));
  const known = new Set(params.knownIds || []);
  const seededHere = isSeededFor(params, specKey);
  const isNew = (id: string) => freshAtOpen.has(id) || (seededHere && !known.has(id));
  const q = query.trim().toLowerCase();
  const rows = q
    ? candidates.filter((s) => `${s.csiNumber || ''} ${s.title || ''}`.toLowerCase().includes(q))
    : candidates;
  const allOn = candidates.length > 0 && candidates.every((s) => on.has(s.id));
  const noneOn = candidates.every((s) => !on.has(s.id));

  const close = () => {
    // Anything that arrived while it was open has been seen too.
    if (writable) {
      const next = markRelatedSeen(raw, candidates, specKey);
      if (next !== raw) write(next, false);
    }
    store.close();
  };

  return (
    <Popover
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <PopoverAnchor virtualRef={anchorRef as React.RefObject<HTMLElement>} />
      <PopoverContent
        ref={contentRef}
        className="dsg-rel-pop"
        align="start"
        side="bottom"
        aria-labelledby={titleId}
        onOpenAutoFocus={(e) => {
          if (!open.focus) e.preventDefault();
        }}
        // Focus going back to the document (an insert, a click on the block)
        // must not dismiss it — a click OUTSIDE still does, and so does Esc.
        onFocusOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          // The document's own Escape (leave the sheet) must not also fire.
          e.stopPropagation();
          closedByEscape.current = true;
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (closedByEscape.current) editor?.commands.focus();
          closedByEscape.current = false;
        }}
      >
        <div className="dsg-rel-head">
          <h3 id={titleId} className="dsg-rel-title">
            Related sections
          </h3>
          {ready && candidates.length > 0 && (
            <span className="dsg-rel-count">
              {on.size} of {candidates.length} included
            </span>
          )}
        </div>

        {!canEdit && (
          <p className="dsg-rel-hint">View only — you can see this list but not change it.</p>
        )}

        {!ready ? (
          <div className="dsg-rel-skel-list" aria-busy="true" aria-label="Loading sections">
            <div className="dsg-rel-skel" />
            <div className="dsg-rel-skel" />
            <div className="dsg-rel-skel" />
          </div>
        ) : candidates.length === 0 ? (
          <p className="dsg-rel-empty">No other sections in this project yet.</p>
        ) : (
          <>
            {candidates.length > SEARCH_THRESHOLD && (
              <Input
                className="dsg-rel-search"
                type="search"
                placeholder="Search sections"
                aria-label="Search sections"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div className="dsg-rel-actions">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!writable || allOn}
                onClick={() => write(setAllRelatedSections(raw, candidates, specKey, true), true)}
              >
                Include all
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!writable || noneOn}
                onClick={() => write(setAllRelatedSections(raw, candidates, specKey, false), true)}
              >
                Exclude all
              </Button>
            </div>
            {rows.length === 0 ? (
              <p className="dsg-rel-empty">No sections match “{query.trim()}”.</p>
            ) : (
              <ul className="dsg-rel-list" aria-label="Sections in this project">
                {rows.map((s) => {
                  const id = `${titleId}-${s.id}`;
                  return (
                    <li key={s.id} className="dsg-rel-row">
                      <Checkbox
                        id={id}
                        checked={on.has(s.id)}
                        disabled={!writable}
                        onCheckedChange={(next) =>
                          write(toggleRelatedSection(raw, candidates, specKey, s.id, next), true)
                        }
                      />
                      <label htmlFor={id} className="dsg-rel-label">
                        <span className="dsg-rel-num">{s.csiNumber || '—'}</span>
                        <span className="dsg-rel-name">{s.title || 'Untitled section'}</span>
                      </label>
                      {isNew(s.id) && (
                        <span
                          style={{ flexShrink: 0 }}
                          title="Added to the project since this list was last opened"
                        >
                          <StatusBadge status="new" />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
